import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { clearAuthStorage, getStoredAuthProfile, getStoredToken, setAuthSession } from "../utils/auth";
import { API_BASE } from "../config/apiBase";
import { clearTenantRequestState } from "../utils/fetchJson";
import { authenticatedFetch } from "../utils/authenticatedRequest";
// import { act } from "react-dom/test-utils";

// Booster login federates to ERP for password and stage authority, then refreshes
// the local progress identity projection. ERP remains the identity authority.
const LOGIN_ENDPOINTS = ["/api/auth/login"];

const buildLoginPayload = (loginId, password) => {
  const id = String(loginId || "").trim();
  if (!id) return null;
  const payload = {
    password,
    app: 'booster',
  };
  const isEmail = id.includes("@");
  if (isEmail) {
    payload.email = id;
    payload.loginId = id;
    payload.login_id = id;
  } else {
    payload.loginId = id;
    payload.login_id = id;
    payload.userId = id;
    payload.username = id;
  }
  return payload;
};

const resolveLoginToken = (payload) => {
  if (!payload || typeof payload !== "object") return "";
  return (
    payload.token ||
    payload.accessToken ||
    payload.jwt ||
    payload.access_token ||
    payload?.data?.token ||
    payload?.data?.accessToken ||
    payload?.data?.jwt ||
    payload?.data?.access_token ||
    payload?.auth?.token ||
    payload?.auth?.accessToken ||
    payload?.auth?.jwt ||
    payload?.auth?.access_token ||
    ""
  );
};

const resolveLoginUser = (payload) => {
  if (!payload || typeof payload !== "object") return {};
  return (
    (payload.user && typeof payload.user === "object" ? payload.user : null) ||
    (payload?.data?.user && typeof payload.data.user === "object" ? payload.data.user : null) ||
    (payload?.profile && typeof payload.profile === "object" ? payload.profile : null) ||
    {}
  );
};

const normalizeText = (value) => String(value || "").trim();

const pickNameField = (user, candidates) => {
  for (const key of candidates) {
    const value = normalizeText(user?.[key]);
    if (value) return value;
  }
  return "";
};

const resolveNameParts = (user = {}, fallbackId = "") => {
  const first = pickNameField(user, ["firstName", "first_name", "firstname", "fname"]);
  const last = pickNameField(user, ["lastName", "last_name", "lastname", "lname"]);
  if (first || last) {
    return { firstName: first, lastName: last };
  }
  const display = pickNameField(user, [
    "displayName",
    "display_name",
    "name",
    "fullName",
    "student_name",
    "studentName",
    "child_name",
    "childName",
  ]);
  if (display) {
    const parts = display.split(/\s+/).filter(Boolean);
    if (parts.length === 1) {
      return { firstName: parts[0], lastName: "" };
    }
    return { firstName: parts[0], lastName: parts.slice(1).join(" ") };
  }
  return { firstName: fallbackId, lastName: "" };
};

const resolveHeaderToken = (response) => {
  if (!response?.headers) return "";
  const candidates = [
    response.headers.get("authorization"),
  ];
  for (const value of candidates) {
    if (!value) continue;
    const trimmed = String(value).trim();
    if (!trimmed) continue;
    if (trimmed.toLowerCase().startsWith("bearer ")) {
      return trimmed.slice(7).trim();
    }
    return trimmed;
  }
  return "";
};

const parseJsonSafe = async (response) => {
  try {
    return await response.json();
  } catch (error) {
    return null;
  }
};

const resolveLoginMessage = (payload) =>
  payload?.errors?.[0]?.msg || payload?.msg || payload?.message || "";

const parseStoredUser = (value) => {
  if (value && typeof value === "object") return value;
  if (typeof value !== "string" || !value.trim()) return {};
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (error) {
    return {};
  }
};

export const buildInitialAuthState = (storedAuthProfile = {}, storedToken = "") => {
  const storedUser = parseStoredUser(storedAuthProfile.user);
  const storedNames = resolveNameParts(storedUser);
  const roleKey = String(storedAuthProfile.role || storedUser.role || "").toUpperCase();
  const role = roleKey === "PARENT" ? "STUDENT" : roleKey;
  const className =
    storedUser.className ||
    storedUser.class ||
    storedUser.class_name ||
    storedUser.classId ||
    storedUser.class_id ||
    storedAuthProfile.className ||
    "";
  const section =
    storedUser.section ||
    storedUser.section_name ||
    storedUser.sectionId ||
    storedUser.section_id ||
    "";

  return {
    firstName: storedNames.firstName || "",
    lastName: storedNames.lastName || "",
    role,
    className,
    section,
    email: storedUser.email || "",
    avatar: storedUser.avatar || storedAuthProfile.avatar || "",
    assignments: storedUser.assignments || null,
    demoMode: Boolean(storedAuthProfile.demoMode || storedUser.demoMode),
    scoreEnabled: storedAuthProfile.scoreEnabled !== false && storedUser.scoreEnabled !== false,
    token: storedToken || "",
    userId: storedUser.userId || storedUser.user_id || storedUser.loginId || storedUser.login_id || storedUser._id || "",
    erpId: storedUser.erpId || storedUser.ID || storedUser.studentId || storedUser.student_id || storedUser._id || "",
    studentId: storedUser.studentId || storedUser.student_id || storedUser._id || "",
    schoolId: storedUser.schoolId || storedUser.school_id || storedUser.underBy || "",
    schoolCode: storedUser.schoolCode || storedUser.school_code || "",
    loading: false,
    error: null,
    msg: "",
    memberships: Array.isArray(storedAuthProfile.memberships) ? storedAuthProfile.memberships : [],
    selectionRequired: Boolean(storedAuthProfile.selectionRequired),
    selectedSchoolCode: storedAuthProfile.selectedSchoolCode || ""
  };
};

const storedAuthProfile = getStoredAuthProfile();
const initialState = buildInitialAuthState(storedAuthProfile, getStoredToken());
export const loginUser = createAsyncThunk("loginUser", async (body) => {
  const loginId = String(body.loginId || body.email || body.userId || body.username || "").trim();
  if (!loginId) {
    throw new Error("Please enter your user ID.");
  }
  if (!body.password) {
    throw new Error("Please enter your password.");
  }

  const payload = buildLoginPayload(loginId, body.password);
  if (!payload) {
    throw new Error("Please enter your user ID.");
  }

  let lastResponse = null;
  let lastJson = null;
  let lastError = null;

  for (const endpoint of LOGIN_ENDPOINTS) {
    try {
      const response = await fetch(`${API_BASE}${endpoint}`, {
        method: "POST",
        cache: "no-store",
        body: JSON.stringify(payload),
        headers: {
          "Content-type": "application/json; charset=UTF-8",
          "Cache-Control": "no-store, no-cache, must-revalidate",
          Pragma: "no-cache",
        },
      });
      const json = await parseJsonSafe(response);

      const headerToken = resolveHeaderToken(response);
      const token = resolveLoginToken(json) || headerToken;
      if (response.ok && token) {
        const user = resolveLoginUser(json);
        const { firstName, lastName } = resolveNameParts(user, loginId);
        return {
          token,
          user,
          firstName,
          lastName,
          role: user?.role,
          organization: user?.organization || user?.school_name || user?.schoolName || "",
          className:
            user?.className ||
            user?.class ||
            user?.class_name ||
            user?.classId ||
            user?.class_id ||
            "",
          section: user?.section || user?.section_name || user?.sectionId || user?.section_id || "",
          avatar: user?.avatar || user?.profilePhoto || user?.photo || "",
          _id: user?._id || user?.id || user?.userId || user?.user_id || "",
          expiresAt: json?.expiresAt || json?.data?.expiresAt || null,
          msg: json?.msg || json?.message || "",
        };
      }

      lastResponse = response;
      lastJson = json;

      if ([404, 405].includes(response.status)) {
        continue;
      }

      // Stop on other errors so we don't spam the login limiter.
      break;
    } catch (error) {
      lastError = error;
    }
  }

  if (!lastResponse && lastError) {
    throw new Error("Network error. Please check your connection before signing in.");
  }

  if (!lastResponse) {
    throw new Error("Login failed.");
  }

  const msg = resolveLoginMessage(lastJson) || "Login failed.";
  throw new Error(msg);
});

export const logoutUser = createAsyncThunk("logoutUser", async () => {
  clearTenantRequestState();
  clearAuthStorage();
});

export const selectTenant = createAsyncThunk("selectTenant", async ({ token, schoolCode }) => {
  const response = await authenticatedFetch(`${API_BASE}/api/auth/session/select`, { method: "POST", cache: "no-store",
    headers: { "Content-Type": "application/json", "x-school-code": schoolCode },
    body: JSON.stringify({ schoolCode }) }, { token, schoolCode });
  const payload = await parseJsonSafe(response);
  if (!response.ok || !payload?.token) throw new Error(resolveLoginMessage(payload) || "School selection failed.");
  return payload;
});

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    startDemoSession: (state) => {
      const demoProfile = {
        firstName: "Demo",
        lastName: "Student",
        role: "STUDENT",
        className: "Demo",
        section: "",
        email: "demo",
        avatar: "",
        assignments: null,
        demoMode: true,
        scoreEnabled: false,
        token: "demo-token",
        loading: false,
        error: null,
        msg: "Demo mode. Scores are not saved.",
      };
      Object.assign(state, demoProfile);
      try {
        sessionStorage.setItem("BOOST_DEMO_ATTEMPTS", "0");
      } catch (error) {
        // no-op
      }
      setAuthSession({
        user: JSON.stringify(demoProfile),
        token: demoProfile.token,
        name: "Demo Student",
        className: "Demo",
        role: "STUDENT",
        avatar: "",
        demoMode: true,
        scoreEnabled: false,
      });
    },
  },
  extraReducers: (builder) => {
    builder.addCase(loginUser.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(loginUser.fulfilled, (state, action) => {
      const { email, loginId, userId, username } = action.meta.arg || {};
      const fallbackId = email || loginId || userId || username || "";
      const loginUserProfile = action.payload.user || {};
      const assignments =
        loginUserProfile && typeof loginUserProfile === "object" ? loginUserProfile.assignments : null;
      const derivedClass =
        assignments?.classTeacher?.className ||
        (Array.isArray(assignments?.classes) ? assignments.classes[0]?.className : "") ||
        "";
      const derivedSection =
        assignments?.classTeacher?.section ||
        (Array.isArray(assignments?.classes) ? assignments.classes[0]?.section : "") ||
        "";
      const className =
        action.payload.className && action.payload.className !== "NA" && action.payload.className !== "0"
          ? action.payload.className
          : derivedClass || action.payload.className;
      const section =
        action.payload.section && action.payload.section !== "NA" && action.payload.section !== "0"
          ? action.payload.section
          : derivedSection || action.payload.section;
      state.email = loginUserProfile.email || fallbackId;
      state.token = action.payload.token;
      const resolvedNames = resolveNameParts(loginUserProfile, fallbackId);
      state.firstName = action.payload.firstName || resolvedNames.firstName;
      state.lastName = action.payload.lastName || resolvedNames.lastName;
      state.className = className;
      state.section = section;
      state.organization = action.payload.organization;
      const roleKey = String(action.payload.role || "").toUpperCase();
      state.role = roleKey === "PARENT" ? "STUDENT" : roleKey;
      state.avatar=action.payload.avatar;
      state._id=action.payload._id;
      state.userId = loginUserProfile.userId || loginUserProfile.user_id || loginUserProfile.loginId ||
        loginUserProfile.login_id || action.payload._id || fallbackId;
      state.erpId = loginUserProfile.erpId || loginUserProfile.ID || loginUserProfile.studentId ||
        loginUserProfile.student_id || loginUserProfile._id || action.payload._id || '';
      state.studentId = loginUserProfile.studentId || loginUserProfile.student_id || loginUserProfile._id || '';
      state.schoolId = loginUserProfile.schoolId || loginUserProfile.school_id || loginUserProfile.underBy || '';
      state.schoolCode = loginUserProfile.schoolCode || loginUserProfile.school_code || '';
      state.assignments = assignments || null;
      state.demoMode = Boolean(loginUserProfile.demoMode || action.payload.demoMode);
      state.scoreEnabled = loginUserProfile.scoreEnabled !== false && action.payload.scoreEnabled !== false && !state.demoMode;
      state.msg = action.payload.msg;
      state.memberships = Array.isArray(loginUserProfile.memberships) ? loginUserProfile.memberships : [];
      state.selectionRequired = loginUserProfile.selectionSource === "selection_required" ||
        (state.memberships.length > 1 && !loginUserProfile.selectedSchoolCode);
      state.selectedSchoolCode = loginUserProfile.selectedSchoolCode || "";
      state.loading = false;
      const fullName = `${state.firstName || ""}${state.lastName ? ` ${state.lastName}` : ""}`.trim();
      setAuthSession({
        user:
          typeof loginUserProfile === "string"
            ? loginUserProfile
            : JSON.stringify(loginUserProfile || fallbackId),
        token: action.payload.token,
        name: fullName || fallbackId,
        className: `${className || ""}${section || ""}`,
        role: state.role || "",
        avatar: action.payload.avatar || "",
        demoMode: state.demoMode,
        scoreEnabled: state.scoreEnabled,
        selectedSchoolCode: state.selectedSchoolCode,
        selectionRequired: state.selectionRequired,
        memberships: state.memberships,
      });
    });
    builder.addCase(loginUser.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
      
    });
    builder.addCase(selectTenant.pending, (state) => { state.loading = true; state.error = null; });
    builder.addCase(selectTenant.fulfilled, (state, action) => {
      state.loading = false;
      state.token = action.payload.token;
      state.selectionRequired = false;
      state.selectedSchoolCode = action.payload.schoolCode || "";
      const profile = { ...(action.payload.user || {}), selectedSchoolCode: state.selectedSchoolCode,
        selectionSource: "explicit_membership", memberships: state.memberships };
      setAuthSession({ user: JSON.stringify(profile), token: state.token,
        name: `${state.firstName || ""} ${state.lastName || ""}`.trim(), className: state.className,
        role: state.role, avatar: state.avatar, demoMode: state.demoMode, scoreEnabled: state.scoreEnabled,
        selectedSchoolCode: state.selectedSchoolCode, selectionRequired: false, memberships: state.memberships });
      clearTenantRequestState();
    });
    builder.addCase(selectTenant.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
      state.selectionRequired = true;
      clearTenantRequestState();
    });
    builder.addCase(logoutUser.fulfilled, () => ({
      ...initialState,
      token: "",
      error: null,
      msg: "",
    }));
  },
});

export const { startDemoSession } = authSlice.actions;
export default authSlice.reducer;

export const selectAuth = (state) => state.auth;
export const getState = (state) => state.auth;

export const selectToken = (state) => state.auth.token;

export const selectUser = (state) => state.auth.user;

export const selectLoading = (state) => state.auth.loading;

export const selectError = (state) => state.auth.error;
